import {
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineMusicNote,
  HiOutlinePlay,
  HiOutlinePause,
  HiOutlineRewind,
  HiOutlineFastForward,
  HiOutlineAnnotation,
  HiOutlineCog,
  HiOutlineX
} from 'react-icons/hi';
import { useRef, useState } from 'react';

const formatTime = (time) => {
  if (!time) return "00:00:00";
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.floor(time % 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export const BottomBar = ({
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  audioPlayer,
  showNotesPanel,
  toggleNotesPanel,
  onOpenSettings
}) => {
  const fileInputRef = useRef(null);
  const [showSeekbar, setShowSeekbar] = useState(false);

  const {
    audioFile,
    isPlaying,
    togglePlayPause,
    skip,
    seekTo,
    currentTime,
    duration,
    playbackSpeed,
    setSpeed,
    loadAudio
  } = audioPlayer;

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) loadAudio(file);
    e.target.value = '';
  };

  return (
    <div
      className="w-full shrink-0 border-t z-50 px-4 py-3 relative"
      style={{ background: 'var(--card)', borderColor: 'var(--card-border)', boxShadow: 'var(--shadow-2)' }}
    >
      {showSeekbar && (
        <div
          className="absolute bottom-full left-0 w-full border-t px-8 py-4 flex items-center justify-center gap-4 z-50"
          style={{ background: 'var(--card)', borderColor: 'var(--card-border)', boxShadow: 'var(--shadow-2)' }}
        >
          <span className="text-xs font-mono min-w-[60px] text-right" style={{ color: 'var(--foreground-muted)' }}>{formatTime(currentTime)}</span>
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={(e) => seekTo(Number(e.target.value))}
            className="slider w-full max-w-2xl h-2 rounded-lg appearance-none cursor-pointer"
            style={{ background: 'var(--background-subtle)' }}
          />
          <span className="text-xs font-mono min-w-[60px]" style={{ color: 'var(--foreground-muted)' }}>{formatTime(duration)}</span>
        </div>
      )}
      <div className="content-width mx-auto flex items-center justify-between gap-4">

        {/* Navigation - Left */}
        <div className="flex items-center gap-2">
          <button
            onClick={onPrev}
            disabled={!hasPrev}
            className="button-ghost p-2 rounded-lg disabled:opacity-30 transition-colors"
          >
            <HiOutlineChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={onNext}
            disabled={!hasNext}
            className="button-ghost p-2 rounded-lg disabled:opacity-30 transition-colors"
          >
            <HiOutlineChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Audio Controls - Center */}
        <div className="flex items-center gap-4 flex-1 justify-center h-12">
          {!audioFile ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="button-secondary flex items-center gap-2 px-4 py-2 rounded-full transition-colors text-sm font-medium"
              >
                <HiOutlineMusicNote className="w-4 h-4" />
                <span>Add Audio</span>
              </button>
            </>
          ) : (
            <div
              className="flex items-center gap-3 px-4 py-1.5 rounded-full border"
              style={{ background: 'var(--background-subtle)', borderColor: 'var(--card-border)' }}
            >
              <button
                onClick={() => setShowSeekbar(!showSeekbar)}
                className="text-[10px] font-mono transition-colors mr-1"
                style={{ color: 'var(--foreground-muted)' }}
              >
                {formatTime(currentTime)} / {formatTime(duration)}
              </button>

              <button onClick={() => skip(-10)} className="button-ghost p-1.5 rounded-full transition-colors">
                <HiOutlineRewind className="w-5 h-5" />
              </button>

              <button
                onClick={togglePlayPause}
                className="button-primary w-10 h-10 flex items-center justify-center rounded-full transition-colors"
              >
                {isPlaying ? <HiOutlinePause className="w-5 h-5" /> : <HiOutlinePlay className="w-5 h-5 pl-0.5" />}
              </button>

              <button onClick={() => skip(10)} className="button-ghost p-1.5 rounded-full transition-colors">
                <HiOutlineFastForward className="w-5 h-5" />
              </button>

              <div className="w-px h-6 mx-1" style={{ background: 'var(--card-border)' }} />

              <button
                onClick={() => {
                  let newSpeed = playbackSpeed + 0.2;
                  if (newSpeed > 2.001) newSpeed = 1.0;
                  setSpeed(parseFloat(newSpeed.toFixed(1)));
                }}
                className="text-xs font-bold w-8 text-center"
                style={{ color: 'var(--foreground-muted)' }}
              >
                {playbackSpeed}x
              </button>

              <button onClick={() => loadAudio(null)} className="button-ghost ml-2 p-1 rounded-full transition-colors">
                <HiOutlineX className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Tools - Right */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggleNotesPanel}
            className={`p-2 rounded-lg transition-colors ${showNotesPanel ? 'button-secondary' : 'button-ghost'}`}
            title={showNotesPanel ? "Hide Notes" : "Show Notes"}
          >
            <HiOutlineAnnotation className="w-6 h-6" />
          </button>

          <button
            onClick={onOpenSettings}
            className="button-ghost p-2 rounded-lg transition-colors"
            title="Reading Settings"
          >
            <HiOutlineCog className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
};
